/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import * as textConstants from '../../constants/StudioTextConstants';
import utils from '../../../../common/utils';

export default class RevokeGraphAccessService extends BaseService {
  constructor() {
    super(constants.REVOKE_GRAPH_ACCESS, 'revokeGraphAccessStatus');
  }

  async handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const { graphId, email } = payload;
    const requestBody = { email };

    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').delete(`/graphs/${graphId}/access`, {
        headers: enhancedHeaders,
        data: requestBody
      });
    }
    return this.getClient('aiadmin').delete(`/graphs/${graphId}/access`, { headers, data: requestBody });
  }

  handleResponse(response) {
    const { data } = response;
    const graphAccessModel = Framework.getStoreRegistry().getStore('graphAccessModel');

    if (data && data.success) {
      // Pass raw response data to model for processing
      graphAccessModel.setRevokeSuccess(data);

      // Show success notification
      const notification = {
        open: true,
        message: data.message || textConstants.GRAPH_ACCESS_REVOKE_SUCCESS,
        type: 'success'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
    } else {
      graphAccessModel.setError(data?.message || textConstants.GRAPH_ACCESS_REVOKE_ERROR, 'revoke');
    }
  }

  handleError(error) {
    const graphAccessModel = Framework.getStoreRegistry().getStore('graphAccessModel');
    const { response } = error;

    if (response) {
      const { status, data } = response;
      if (status === 401) {
        utils.redirectToLogin();
      }
      const errorMessage = data?.error || data?.message || textConstants.GRAPH_ACCESS_REVOKE_ERROR;
      graphAccessModel.setError(errorMessage, 'revoke');
      const notification = {
        open: true,
        message: errorMessage,
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
    } else if (error.message === 'timeout of 60000ms exceeded') {
      graphAccessModel.setError('Request timeout. Please try again.', 'revoke');
      const notification = {
        open: true,
        message: 'Request timeout. Please try again.',
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
    } else {
      graphAccessModel.setError(error.message || textConstants.GRAPH_ACCESS_REVOKE_ERROR, 'revoke');
    }
  }
}
